import React, { forwardRef, useMemo } from 'react';
import { FormControl, FormHelperText } from '@mui/material';
import ReactSelect, { components } from 'react-select';
import { translate as t } from 'hooks/useTranslations';

interface LevelOption {
  value: 'AGENCY' | 'JURISDICTION' | 'GLOBAL' | null;
  label: string;
}

export type DtbLevelFilterProps = {
  allowBlank?: boolean;
  id: string;
  label?: string;
  value?: (string | null)[];
  onChange: (value: (string | null)[]) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: any;
  helperText?: string | undefined;
  size?: string;
  lexipolOnly?: boolean;
  showAllOption?: boolean;
};

const DtbLevelFilter = forwardRef(
  (
    {
      allowBlank = false,
      id,
      label,
      value = [],
      onChange,
      placeholder,
      disabled = false,
      required,
      error,
      helperText,
      size,
      lexipolOnly = false,
      showAllOption = false,
    }: DtbLevelFilterProps,
    ref
  ) => {
    const SingleValue = ({ children, ...props }: any) => (
      <components.SingleValue {...props}>DTB Level: {children}</components.SingleValue>
    );

    const levelOptions = useMemo(() => {
      const options: LevelOption[] = [
        ...(allowBlank ? [{ value: '' as any, label: placeholder ?? '-- Select a level --' }] : []),
        ...(showAllOption ? [{ value: null, label: t('dtbs.library.level.ALL') }] : []),
        { value: 'GLOBAL', label: t('dtbs.library.level.GLOBAL') },
        { value: 'JURISDICTION', label: t('dtbs.library.level.JURISDICTION') },
      ];

      if (!lexipolOnly) {
        options.push({ value: 'AGENCY', label: t('dtbs.library.level.AGENCY') });
      }

      return options;
    }, [allowBlank, placeholder, lexipolOnly, showAllOption, t]);

    const selectedOptions = useMemo(() => {
      return levelOptions.filter((x) => (value ?? []).includes(x.value));
    }, [value, levelOptions]);

    return (
      <FormControl
        key={id}
        fullWidth
        error={!!error}
        sx={{
          p: 0,
        }}
      >
        <ReactSelect
          name={id}
          value={selectedOptions}
          options={levelOptions}
          placeholder={label || placeholder}
          menuPortalTarget={document.body}
          components={{ SingleValue }}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, width: 300 }),
            input: (base) => ({
              ...base,
              boxShadow: 'none',
              outline: 0,
              minHeight: size === 'small' ? 26 : 45,
            }),
          }}
          menuPosition='fixed'
          menuPlacement='auto'
          isSearchable={false}
          isDisabled={disabled}
          onChange={(newValue) => {
            if (Array.isArray(newValue)) {
              onChange((newValue ?? []).map((x) => x.value));
            } else {
              onChange(!!newValue ? [newValue.value] : []);
            }
          }}
        />
        {!!error && <FormHelperText error>{error.message}</FormHelperText>}
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

export default DtbLevelFilter;
